import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

// Define colors for each status.
const COLORS = {
  Confirmed: "#22C55E",
  Declined: "#FF4D4F",
  Pending: "#F8CB00",
  Cancelled: "#9CA3AF"
};





// ...existing code...
const BarChartComponent = ({ statistics, monthlyData, onFilterChange }) => {
   const { t, i18n } = useTranslation("dash");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");

  // Check if current language is RTL
  const isRTL = i18n.language === 'ar' || i18n.dir() === 'rtl';

  // Dynamic style object for RTL/LTR support for select elements
  const selectStyle = {
    padding: "6px 16px",
    borderRadius: "8px",
    border: "1px solid var(--Secondry-10, #E9EAEB)",
    color: "var(--Light-text, #6B7280)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    backgroundImage: `url("/Icon-right.svg")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: isRTL ? "left 10px center" : "right 10px center",
    paddingLeft: isRTL ? "40px" : "16px",
    paddingRight: isRTL ? "16px" : "40px",
    minWidth: "100px",
    outline: "none",
    cursor: "pointer",
    direction: isRTL ? "rtl" : "ltr",
    textAlign: isRTL ? "right" : "left"
  };

  // Safely grab years array
  const yearsList = Array.isArray(t("years", { returnObjects: true }))
    ? t("years", { returnObjects: true })
    : [];

  // Safely grab months object
  const monthsObj = typeof t("months", { returnObjects: true }) === "object"
    ? t("months", { returnObjects: true })
    : {};

  // Build chart data
  const chartData =
    monthlyData && monthlyData.length > 0
      ? monthlyData
      : statistics
      ? [
          {
            month: t("legend.Confirmed"),
            confirmed: statistics.confirmed?.count ?? 0,
            declined: statistics.declined?.count ?? 0,
            pending: statistics.pending?.count ?? 0,
            cancelled: statistics.cancelled?.count ?? 0
          }
        ]
      : [];

  if (chartData.length === 0) {
    return <div>{t("noData")}</div>;
  }

  // Custom legend renderer
  const renderCustomizedLegend = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "start",
        margin: "10px 0",
        flexDirection: "row-reverse" // RTL alignment for the legend
      }}
    >
      {Object.keys(COLORS).map((key) => (
        <div
          key={key}
          style={{
            display: "flex",
            alignItems: "center",
            margin: "0 10px",
            flexDirection: "row-reverse" // RTL alignment for each item
          }}
        >
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              backgroundColor: COLORS[key],
              border: "1px solid #ccc",
              marginRight: 8
            }}
          />
          <div style={{ fontSize: 12, color: "#333", fontWeight: "bold" }}>
            {t(`legend.${key}`)}
          </div>
        </div>
      ))}
    </div>
  );

  // Handlers
  const handleYearChange = (e) => {
    const newYear = e.target.value;
    setSelectedYear(newYear);
    onFilterChange?.(newYear, selectedMonth);
  };

  const handleMonthChange = (e) => {
    const newMonth = e.target.value;
    setSelectedMonth(newMonth);
    onFilterChange?.(selectedYear, newMonth);
  };

  return (
    <div dir="rtl"> {/* Set the entire component's direction to RTL */}
      {/* Title and filters */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20
        }}
      >
        <h3 className="dashboard-subtitle" style={{ margin: 0 }}>
          {t("title__")}
        </h3>
        <div style={{ display: "flex", gap: "10px" }}>
          <select
            value={selectedYear}
            onChange={handleYearChange}
            style={selectStyle}
          >
            <option value="">{t("filters.yearPlaceholder")}</option>
            {yearsList.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <select
            value={selectedMonth}
            onChange={handleMonthChange}
            style={selectStyle}
          >
            <option value="">{t("filters.monthPlaceholder")}</option>
            {Object.entries(monthsObj).map(([val, label]) => (
              <option key={val} value={val}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {renderCustomizedLegend()}

      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 40 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="month" axisLine={false} tickLine={false} />
          <YAxis axisLine={false} tickLine={false} />
          <Tooltip />
          <Bar dataKey="confirmed" fill={COLORS.Confirmed} barSize={10} />
          <Bar dataKey="declined" fill={COLORS.Declined} barSize={10} />
          <Bar dataKey="pending" fill={COLORS.Pending} barSize={10} />
          <Bar dataKey="cancelled" fill={COLORS.Cancelled} barSize={10} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BarChartComponent;
